import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { authApi } from "../api";

const schema = z
  .object({
    current_password: z.string().min(1, "required"),
    new_password: z.string().min(8, "at least 8 characters"),
    confirm_password: z.string().min(8),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    message: "passwords do not match",
    path: ["confirm_password"],
  });
type FormValues = z.infer<typeof schema>;

export default function AccountPage() {
  const me = useQuery({ queryKey: ["me"], queryFn: authApi.me });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function submit(v: FormValues) {
    try {
      await authApi.changePassword(v.current_password, v.new_password);
      toast.success("Password changed");
      reset();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Change failed");
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-10 py-10">
      <header className="mb-7">
        <h1 className="display text-[36px] font-medium tracking-tight">
          My <span className="italic">account</span>
        </h1>
        <p className="text-[13.5px] text-ink-500 mt-1.5">
          Signed in as <span className="font-mono">{me.data?.user?.username}</span> ·{" "}
          <span className="capitalize">{me.data?.user?.role}</span>
        </p>
      </header>

      <form onSubmit={handleSubmit(submit)} className="surface p-6 space-y-4 max-w-md">
        <h2 className="display text-[18px] font-medium tracking-tight">
          Change <span className="italic">password</span>
        </h2>
        <div>
          <label className="label">Current password</label>
          <input type="password" className="input" {...register("current_password")} autoComplete="current-password" />
          {errors.current_password && <p className="text-[11.5px] text-accent-600 mt-1">{errors.current_password.message}</p>}
        </div>
        <div>
          <label className="label">New password</label>
          <input type="password" className="input" {...register("new_password")} autoComplete="new-password" />
          {errors.new_password && <p className="text-[11.5px] text-accent-600 mt-1">{errors.new_password.message}</p>}
        </div>
        <div>
          <label className="label">Confirm new password</label>
          <input type="password" className="input" {...register("confirm_password")} autoComplete="new-password" />
          {errors.confirm_password && <p className="text-[11.5px] text-accent-600 mt-1">{errors.confirm_password.message}</p>}
        </div>
        <div className="pt-3 border-t border-ink-100 mt-5 flex justify-end">
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? "Changing…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}
